<template>
  <div class="endorsement-page py-4">
    <div class="container" v-if="applicantData">
      <div class="document-container card border-0 shadow-sm">
        <!-- Header -->
        <div class="doc-header p-4 text-white">
          <h2 class="mb-1" v-if="screeningType === 'employment'">Employment Reference Request</h2>
          <h2 class="mb-1" v-else-if="screeningType === 'education'">Education Verification Request</h2>
          <h2 class="mb-1" v-else-if="screeningType === 'unemployment' && applicantData.statusable.company_name === 'Unemployment'">Unemployment Verification Request</h2>
          <h2 class="mb-1" v-else-if="screeningType === 'unemployment' && applicantData.statusable.company_name === 'Other'">Other Verification Request</h2>
          <h2 class="mb-1" v-else-if="screeningType === 'self_employment'">Self Employment Verification Request</h2>
          <p class="mb-0 small opacity-75">BS 7858 pre-employment screening / employment verification request</p>
        </div>

        <div class="p-4">
          <!-- Company Info centered -->
          <div class="company-section mb-4 text-center">
            <div v-if="applicantData.applicant.company.logo" class="company-logo mb-3">
              <img :src="applicantData.applicant.company.logo" alt="Logo" style="max-height: 80px;" />
            </div>
            <div class="company-info">
              <h4 class="fw-bold mb-1">{{ applicantData.applicant.company.company_name }}</h4>
              <p class="mb-1 text-muted small">
                {{ [applicantData.applicant.company.premises, applicantData.applicant.company.address_line_1, applicantData.applicant.company.city, applicantData.applicant.company.country].filter(Boolean).join(', ') }}
                <span v-if="applicantData.applicant.company.postal_code"> - {{ applicantData.applicant.company.postal_code }}</span>
              </p>
              <p class="mb-0 text-muted small">
                <span><strong>Email:</strong> {{ applicantData.applicant.company.company_email }}</span>
                <span v-if="applicantData.applicant.company.company_phone"> | <strong>Phone:</strong> {{ applicantData.applicant.company.company_phone }}</span>
              </p>
            </div>
          </div>

          <div class="greeting-section mb-4">
            <p>Dear {{ applicantData.statusable.contact_name || 'Sir / Madam' }},</p>
            <p v-if="screeningType === 'employment'">
              We are currently undertaking pre-employment screening in respect of the applicant named below, who has stated that they were employed by your organisation during the period shown.
            </p>
            <p v-else-if="screeningType === 'education'">
              We are currently undertaking academic verification in respect of the applicant named below.
            </p>
            <p v-else >
              We are currently undertaking verification in respect of the applicant named below.
            </p>
            <p class="small text-muted">
              We should be grateful if you would confirm the details requested below for the purpose of verifying the individual's employment history. This request forms part of our screening process and the information provided will be treated in confidence and used only for employment vetting and verification purposes.
            </p>
          </div>

          <!-- Section: Applicant Details -->
          <div class="section-container mb-4">
            <div class="section-header px-3 py-2 fw-bold text-center text-uppercase" style="text-decoration: underline;">
              <span v-if="screeningType === 'employment'">Agency Employment Reference</span>
              <span v-else-if="screeningType === 'education'">Education Reference</span>
              <span v-else-if="screeningType === 'self_employment'">Self Employment Reference</span>
              <span v-else-if="screeningType === 'unemployment' && applicantData.statusable.company_name === 'Other'">Other Reference</span>
              <span v-else-if="screeningType === 'unemployment'">Unemployment Reference</span>
              <span v-else>{{ label }} Reference</span>
            </div>
            
            <div class="mt-3">
              <h6 class="fw-bold mb-2">APPLICANT INFORMATION</h6>
              <table class="table table-bordered mb-3">
                <tbody>
                  <tr>
                    <td class="bg-light w-25 fw-bold">Applicant Name</td>
                    <td><u>{{ applicantData.applicant.full_name }}</u></td>
                  </tr>
                  <tr>
                    <td class="bg-light w-25 fw-bold">Applicant Date of Birth</td>
                    <td><u>{{ applicantData.applicant.date_of_birth || '____________________' }}</u></td>
                  </tr>
                  <tr>
                    <td class="bg-light w-25 fw-bold">Applicant NI Number</td>
                    <td><u>{{ applicantData.applicant.n_i_number || '____________________' }}</u></td>
                  </tr>
                </tbody>
              </table>

              <h6 class="fw-bold mb-2">INFORMATION PROVIDED BY THE APPLICANT</h6>
              <table class="table table-bordered mb-3">
                <tbody>
                  <tr>
                    <td class="bg-light w-25 fw-bold">Date from</td>
                    <td><u>{{ applicantData.statusable.start_date_formatted }}</u></td>
                  </tr>
                  <tr>
                    <td class="bg-light w-25 fw-bold">Date to</td>
                    <td><u>{{ applicantData.statusable.end_date_formatted }}</u></td>
                  </tr>
                </tbody>
              </table>

              <h6 class="fw-bold mb-2">PLEASE CONFIRM BELOW INFORMATION</h6>
              <table class="table table-bordered mb-3">
                <tbody>
                  <tr>
                    <td class="bg-light w-25 fw-bold">{{  label }}{{label === 'Employment'? '/placement': ''}} start date</td>
                    <td><u>{{ applicantData.statusable.start_date_formatted }}</u></td>
                  </tr>
                  <tr>
                    <td class="bg-light w-25 fw-bold">{{  label }}{{label === 'Employment'? '/placement': ''}} end date</td>
                    <td><u>{{ applicantData.statusable.end_date_formatted }}</u></td>
                  </tr>

                  <tr v-if="screeningType === 'employment'">
                    <td class="bg-light w-25 fw-bold">Job title</td>
                    <td><u>{{ applicantData.statusable.title }}</u></td>
                  </tr>
                  <tr>
                    <td class="bg-light w-25 fw-bold">Client/location services were delivered</td>
                    <td><u>{{ applicantData.statusable.company_name || applicantData.statusable.name }}</u></td>
                  </tr>
                  <tr>
                    <td class="bg-light w-25 fw-bold">Reason for leaving</td>
                    <td><u>{{ applicantData.statusable.reason_for_leaving || 'Not provided' }}</u></td>
                  </tr>
                </tbody>
              </table>

              <template v-if="screeningType !== 'unemployment'">
                <h6 class="fw-bold mb-2">YOUR DETAILS</h6>
                <table class="table table-bordered mb-3">
                  <tbody>
                    <tr>
                      <td class="bg-light w-25 fw-bold">Full name</td>
                      <td><u>{{ applicantData.statusable.contact_name || applicantData.statusable.accountant_name }}</u></td>
                    </tr>
                    <tr>
                      <td class="bg-light w-25 fw-bold">Contact number</td>
                      <td><u>{{ applicantData.statusable.contact_number || '____________________' }}</u></td>
                    </tr>
                    <tr>
                      <td class="bg-light w-25 fw-bold">Email address</td>
                      <td><u>{{ applicantData.statusable.contact_email || '____________________' }}</u></td>
                    </tr>
                    <tr>
                      <td class="bg-light w-25 fw-bold">Employment Agency Name</td>
                      <td><u>{{ applicantData.applicant.company.company_name }}</u></td>
                    </tr>
                  </tbody>
                </table>
              </template>
            </div>
          </div>

          <!-- Section: Verification Requested (Inputs) -->
          <div class="section-container mb-4">
            <div class="section-header px-3 py-2 fw-bold">Verification Requested</div>
            <form ref="form" :data-url="addurl" class="mb-0">
              <app-overlay-loader v-if="preloader" />
              <div :class="{ 'loading-opacity': preloader }">
                <table class="table table-bordered mb-0">
                  <tbody>
                    <template v-if="screeningType === 'employment'">
                      <tr>
                        <td class="bg-light w-50 fw-bold">
                          To the best of your knowledge, is there anything known about the person named in this reference which suggests a lack of integrity or reliability?
                        </td>
                        <td>
                          <app-input type="radio"
                                     :list="[{id: 'yes', value: 'Yes'}, {id: 'no', value: 'No'}]"
                                     v-model="inputs.integrity_reliability"
                                     :required="true"
                          />
                        </td>
                      </tr>
                      <tr v-if="inputs.integrity_reliability === 'yes'">
                        <td class="bg-light w-50 fw-bold">If you answered Yes, please state the reason or reasons:</td>
                        <td>
                          <app-input type="textarea"
                                     v-model="inputs.integrity_reliability_reason"
                                     placeholder="Enter reason(s)"
                          />
                        </td>
                      </tr>
                    </template>
                      <tr>
                        <td class="bg-light w-50 fw-bold">Are the {{  label.toLowerCase() }} start and end dates correct?</td>
                        <td>
                          <app-input type="radio"
                                     :list="[{id: 'yes', value: 'Yes'}, {id: 'no', value: 'No'}]"
                                     v-model="inputs.dates_confirmed"
                                     :required="true"
                          />
                        </td>
                      </tr>
                      <tr v-if="inputs.dates_confirmed === 'no'">
                         <td class="bg-light w-50 fw-bold">If you answered No, please state the correct dates or reasons:</td>
                         <td>
                           <app-input type="textarea"
                                      v-model="inputs.additional_information"
                                      placeholder="Enter correct dates or reason(s)"
                           />
                         </td>
                       </tr>
                      <tr>
                        <td class="bg-light w-50 fw-bold">Are the above details correct?</td>
                      <td>
                        <app-input type="radio"
                                   :list="[{id: 'yes', value: 'Yes'}, {id: 'no', value: 'No'}]"
                                   v-model="inputs.are_dates_correct"
                                   :required="true"
                        />
                      </td>
                    </tr>
                    <tr v-if="inputs.are_dates_correct === 'no'">
                      <td class="bg-light w-50 fw-bold">Endorsement Remarks / Confirmed Details</td>
                      <td>
                        <app-input id="inputs_endorsement_remarks"
                                   type="text-editor"
                                   placeholder="Enter Remarks"
                                   v-model="inputs.endorsement_remarks" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </form>
          </div>

          <!-- Section: Respondent Details -->
          <div class="section-container mb-4">
            <div class="section-header px-3 py-2 fw-bold">Respondent Details</div>
            <table class="table table-bordered mb-0">
              <tbody>
                <tr>
                  <td class="bg-light w-25 fw-bold">Signed By</td>
                  <td>
                    <app-input id="inputs_signed_by" type="text" v-model="inputs.signed_by" placeholder="Enter Name" :required="true" />
                  </td>
                  <td class="bg-light w-25 fw-bold">Designation</td>
                  <td>
                    <app-input id="inputs_designation" type="text" v-model="inputs.designation" placeholder="Enter Designation" />
                  </td>
                </tr>
                <tr>
                  <td class="bg-light w-25 fw-bold">Date</td>
                  <td colspan="3">
                    <div class="w-50">
                      <app-input id="inputs_endorsed_at" type="date" v-model="inputs.endorsed_at" :required="true" />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td class="bg-light w-25 fw-bold">Signature</td>
                  <td colspan="3">
                    <div v-if="!inputs.signature">
                      <VueSignaturePad id="signature"
                                       width="100%"
                                       height="150px"
                                       ref="signaturePad"
                                       class="border rounded bg-white"
                                       :options="{ onBegin: () => { $refs.signaturePad.resizeCanvas() } }"
                      />
                      <div class="mt-2 d-flex justify-content-end">
                        <button @click.prevent="undo" class="btn btn-sm btn-outline-danger">Undo</button>
                      </div>
                    </div>
                    <div v-else>
                      <img :src="inputs.signature" alt="Signature" style="max-width: 200px;" />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Submit Button -->
          <div class="text-center mt-4" v-if="endorsement.status !== 'endorsed'">
            <button type="submit" class="btn btn-primary px-5 py-2 fw-bold" @click.prevent="submitForm">
              Submit Endorsement
            </button>
          </div>

          <div class="text-center mt-4" v-else>
            <div class="alert alert-success d-inline-block px-5">
              <i class="fa fa-check-circle mr-2"></i> This reference has been successfully endorsed.
            </div>
          </div>

          <div class="mt-4 pt-3 border-top text-center text-muted x-small">
            This communication is confidential and intended solely for the recipient organisation for the purpose of confirming previous {{  label.toLowerCase() }} history.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {FormMixin} from '../../../../core/mixins/form/FormMixin.js';
import {SCREENING_ENDORSEMENT_UPDATE, SCREENING_ENDORSEMENT_DETAIL} from "../../../Config/ScreeningAppUrl.js";
import { axiosGet } from "@/app/Helpers/AxiosHelper";

  export default {
    name: "ScreeningEndorsement",
    mixins: [FormMixin],
    props: {
      screeningType: {
        type: String,
        default: 'employment'
      }
    },
    data() {
      return {
        signaturePath: '',
        inputs: {
          endorsed_at: new Date(),
          integrity_reliability: 'no',
          integrity_reliability_reason: '',
          dates_confirmed: 'no',
          additional_information: '',
          are_dates_correct: 'no',
          endorsement_remarks: `
                <p>
                  <label>
                    Start Date:
                    </label>
                    <p></p>
                </p>

                <p>
                  <label>
                    End Date:
                  </label>
                  <p></p>
                </p>
                <p>
                  <label>
                   Remarks: (Please state if the information matches with your records)
                  </label>
                  <p></p>
                </p>
              `,

        },
        preloader: false,
        companyInfo: {},
        screeningEndorsementId: null,
        addurl: SCREENING_ENDORSEMENT_UPDATE,
        applicantData: null,
        endorsement: {},
        isAdmin: false,
        label: '',
      };
    },
    created() {
      this.companyInfo = this.$attrs['companyData-prop'];
      this.endorsement = this.$attrs['endorsement-prop'];
      this.screeningEndorsementId = this.$attrs['screeningendorsementid'];
      this.isAdmin = this.$attrs['isadmin'];
      this.getScreeningEndorsementDetail(this.screeningEndorsementId);
      this.setEndorsement();

    },

    methods: {
      submitForm() {
        const {isEmpty, data} = this.$refs.signaturePad.saveSignature();
        if (data != null) {
          this.inputs.signature = data;
          this.inputs.screeningEndorsementId = this.screeningEndorsementId;

          let formData = new FormData();
          Object.keys(this.inputs).forEach((key) => {
            formData.append(key, this.inputs[key]);
          });
          this.save(formData)
        } else {
          this.$toastr.e("Signature is required.");
        }
      },
      beforeSubmit() {
        this.preloader = true;
      },
      afterSuccess(response) {
        this.preloader = false;
        // Redirect to success page
        window.location.href = '/screening-endorsement/success';
      },
      afterFinalResponse() {
        this.preloader = false;
      },
      afterError(response) {
        console.error("Error caught", response);
        // this.$toastr.e(response.response.data.message);
        this.preloader = false;
      },
      undo() {
        this.$refs.signaturePad.undoSignature();
      },
      getScreeningEndorsementDetail(screeningEndorsementId) {
        axiosGet(SCREENING_ENDORSEMENT_DETAIL + screeningEndorsementId)
            .then((res) => {
              this.applicantData = res.data;
              switch (this.screeningType) {

                case 'employment':
                  this.label = 'Employment';
                  break;

                case 'education':
                  this.label = 'Education';
                  break;

                case 'self_employment':
                  this.label = 'Self Employment';
                  break;

                case 'unemployment':

                  this.label =
                      this.applicantData.statusable.company_name === 'Other'
                          ? 'Other'
                          : 'Unemployment';

                  break;

                default:
                  this.label = '';
              }
            })
            .catch((error) => {

            });
      },
      setEndorsement() {
        if (this.isAdmin || this.endorsement.status === 'endorsed') {
          this.inputs = {
            ...this.endorsement,
            integrity_reliability: this.endorsement.integrity_reliability || 'no',
            integrity_reliability_reason: this.endorsement.integrity_reliability_reason || '',
            dates_confirmed: this.endorsement.dates_confirmed || 'no',
            additional_information: this.endorsement.additional_information || '',
            are_dates_correct: this.endorsement.are_dates_correct || 'no',
            endorsed_at: this.endorsement.endorsed_at ? new Date(this.endorsement.endorsed_at) : new Date()
          }
        }
      },
    },




  };
</script>

<style scoped>
.endorsement-page {
  background-color: #f4f7f9;
  min-height: 100vh;
}
.document-container {
  max-width: 900px;
  margin: 0 auto;
  background: #fff;
}
.doc-header {
  background-color: #1a3a5f;
}
.section-header {
  background-color: #e9ecef;
  border: 1px solid #dee2e6;
  border-bottom: none;
  color: #333;
}
.section-container .table-bordered {
  border-color: #dee2e6;
}
.section-container .table td {
  padding: 12px;
  vertical-align: middle;
}
.x-small {
  font-size: 0.75rem;
}
.loading-opacity {
  opacity: 0.5;
}
</style>